import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import { useAppSelector } from '@store';
import {
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  useGetList,
  useQueryState,
  useFilters,
  useScroll,
  formatDate,
  Table,
  SchemaFilterType,
  usePrevious,
  IconArrowDiagonal,
  IconClock,
  IconCopyTwo,
  IconSelected,
} from '@ui/src';
import {
  useLazyGetPspPartnerQuery,
  useLazyGetPspIntegrationsLogsQuery,
} from './store/services';
import {
  integrationLogsColumns,
  integrationLogsColumnsLabels,
  integrationLogsSchema,
  integrationLogsFiltersSchema,
} from './variables/integration-logs';
import { IntegrationLog } from './types';
import Card from '../../components/partial/card/card';

import cn from './bank-and-psp.module.css';

const formatIntegrationLogs = (
  list: IntegrationLog[],
  onCopy: (text: string) => void,
  copyIsSuccess?: boolean,
): IntegrationLog[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let statusCodeClassName = '';
    let responseTimeClassName = '';
    let type;

    if (item?.statusCode) {
      if (item?.statusCode?.toString()?.charAt(0) === '2') {
        statusCodeClassName = cn.successStatus;
      } else if (item?.statusCode?.toString()?.charAt(0) === '3') {
        statusCodeClassName = cn.redirectStatus;
      } else if (item?.statusCode?.toString()?.charAt(0) === '4') {
        statusCodeClassName = cn.errorStatusCode;
      } else if (item?.statusCode?.toString()?.charAt(0) === '5') {
        statusCodeClassName = cn.internalErrorStatusCode;
      }
    }

    if (item?.requestTime && item?.responseTime) {
      const difference =
        new Date(item?.responseTime).getTime() -
        new Date(item.requestTime).getTime();

      if (difference >= 1500 && difference <= 2500) {
        responseTimeClassName = cn.yellowIcon;
      } else if (difference > 2500) {
        responseTimeClassName = cn.redIcon;
      }
    } else if (!item.responseTime) {
      responseTimeClassName = cn.redIcon;
    }

    if (item?.types) {
      type = (
        <div className={cn.logType}>
          <IconArrowDiagonal
            width={10}
            height={10}
            className={item?.types === 'WEBHOOK' ? cn.webhookIcon : ''}
          />
          {`${item?.types?.charAt(0)}${item?.types?.slice(1).toLowerCase()}`}
        </div>
      );
    } else {
      type = '-';
    }

    const render: Record<string, React.JSX.Element> = {
      types: <div>{type}</div>,
      url: (
        <div className={cn.urlEventTypeWrapper}>
          {item?.eventType && (
            <div className={cn.listEventTypeWrapper}>
              <div className={cn.eventType}>{item.eventType}</div>
            </div>
          )}
          {item?.url && (
            <div className={cn.logUrl}>
              <span>{item.url}</span>
            </div>
          )}
          {!item?.eventType && !item?.url && '-'}
        </div>
      ),
      transactionIds: (
        <div className={cn.transactionId}>{item?.transactionId || '-'}</div>
      ),
      requestTime: (
        <div>
          {item?.requestTime ? formatDate(item.requestTime, true, true) : '-'}
        </div>
      ),
      responseTime: (
        <div className={cn.logsResponseTime}>
          {item?.responseTime ? formatDate(item.responseTime, true, true) : '-'}
          <div className={cn.logsResponseTimeIcon}>
            <div className={cn.logsResponseLatency}>{`Latency: ${
              item?.requestTime && item?.responseTime
                ? new Date(item?.responseTime).getTime() -
                  new Date(item.requestTime).getTime()
                : '-'
            } ms`}</div>
            <IconClock
              width={14}
              height={14}
              className={responseTimeClassName}
            />
          </div>
        </div>
      ),
      statusCode: (
        <div>
          {item?.statusCode ? (
            <div className={`${cn.logStatusCode} ${statusCodeClassName}`}>
              {item.statusCode}
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
      responseBody: (
        <div className={cn.logsResponseBodyWrapper}>
          <div className={cn.logsResponseBody}>
            <span>{item?.responseBody || '-'}</span>
          </div>
          <button
            type="button"
            className={cn.copyListButton}
            onClick={() =>
              onCopy(
                item?.responseBody
                  ? JSON.stringify(
                      item?.responseBody.includes('{') &&
                        item?.responseBody.charAt(0) === '{'
                        ? JSON.parse(item?.responseBody)
                        : item?.responseBody,
                      null,
                      2,
                    )
                  : '-',
              )
            }
          >
            {copyIsSuccess ? (
              <IconSelected />
            ) : (
              <IconCopyTwo width={16} height={16} />
            )}
          </button>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const PspIntegrationLogs = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [lastFiveMinActive, setLastFiveMinActive] = useState(false);
  const [lastFiveteenMinActive, setLastFiveteenMinActive] = useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const {
    bankAndPsp: {
      info: { generalInfo, integrationLogs, integrationLogsPagination },
    },
    configurationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);
  const [getPartner, { isLoading, isFetching }] = useLazyGetPspPartnerQuery();
  const [
    getIntegrationsLogs,
    { isLoading: logsIsLoading, isFetching: logsIsFetching },
  ] = useLazyGetPspIntegrationsLogsQuery();

  const { bankId } = useParams();

  const { filters, onFilterChange } = useQueryState(integrationLogsSchema);

  const prevFilters = usePrevious(filters);

  const onCopy = (text: string) => {
    setCopyIsSuccess(true);

    copy(text);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<IntegrationLog>({
      getList: getIntegrationsLogs,
      onFilterChange,
      onSortChange: () => {},
      itemId: bankId,
      schema: integrationLogsSchema,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: integrationLogsFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatIntegrationLogs(
      integrationLogs as IntegrationLog[],
      onCopy,
      copyIsSuccess,
    );

    return data;
  }, [integrationLogs, filters, onCopy, copyIsSuccess]);

  const getPartnerAction = useCallback(() => {
    getPartner(bankId as string);
  }, [bankId]);

  useScroll({
    element: scrollRef,
    requestInProgress: logsIsFetching,
  });

  useEffect(() => {
    if (bankId && activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      if (!generalInfo) {
        getPartnerAction();
      }
    }
  }, [bankId, generalInfo]);

  useEffect(() => {
    if (
      bankId &&
      generalInfo &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_LOGS_LIST_BY_PSP_PARTNER,
      ) &&
      filters &&
      !integrationLogs
    ) {
      const nextFilters = { ...filters };

      const responeTimeStorage = localStorage.getItem('responseTime');

      if (responeTimeStorage) {
        const [from, to] = JSON.parse(responeTimeStorage);

        const difference =
          (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60);

        if (difference === 5) {
          setLastFiveMinActive(true);
        } else if (difference === 15) {
          setLastFiveteenMinActive(true);
        }

        nextFilters.responseTime = {
          type: SchemaFilterType.DATE_RANGE,
          value: [from, to],
        };
      }

      getListAction({
        filters: nextFilters,
        withoutTimeZone: true,
      });
    }
  }, [bankId, activePermissions, generalInfo, filters, integrationLogs]);

  useEffect(() => {
    if (prevFilters?.responseTime && !filters.responseTime) {
      setLastFiveMinActive(false);
      setLastFiveteenMinActive(false);
      localStorage.removeItem('responseTime');
    }
  }, [prevFilters, filters]);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.PSP_INTEGRATION_LOGS_LIST_BY_PSP_PARTNER,
    ) ||
    !activePermissions?.includes(PermissionEnum.PSP_LIST)
  ) {
    content = <PermissionDenied />;
  } else if (isLoading || isFetching) {
    content = (
      <div className={cn.bankAndPspLoader}>
        <IconLoader />
      </div>
    );
  } else {
    let tableContent;

    if (logsIsLoading) {
      tableContent = (
        <div className={cn.logsTableLoader}>
          <IconLoader />
        </div>
      );
    } else {
      tableContent = (
        <div className={cn.integrationLogsTableWrapper}>
          <div className={cn.filtersButtons}>
            <button
              type="button"
              className={`${cn.filterButton} ${
                filters?.types?.value?.includes('WEBHOOK') ? cn.active : ''
              }`}
              onClick={() => {
                const nextTypes = [];

                if (filters?.types?.value?.includes('REQUEST')) {
                  nextTypes.push('REQUEST');
                }

                if (!filters?.types?.value?.includes('WEBHOOK')) {
                  nextTypes.push('WEBHOOK');
                }

                onFilter(
                  {
                    ...filters,
                    types: {
                      value: nextTypes,
                      type: SchemaFilterType.STRING,
                    },
                  },
                  true,
                );
              }}
            >
              Webhooks
            </button>
            <button
              type="button"
              className={`${cn.filterButton} ${
                filters?.types?.value?.includes('REQUEST') ? cn.active : ''
              }`}
              onClick={() => {
                const nextTypes = [];

                if (filters?.types?.value?.includes('WEBHOOK')) {
                  nextTypes.push('WEBHOOK');
                }

                if (!filters?.types?.value?.includes('REQUEST')) {
                  nextTypes.push('REQUEST');
                }

                onFilter(
                  {
                    ...filters,
                    types: {
                      value: nextTypes,
                      type: SchemaFilterType.STRING,
                    },
                  },
                  true,
                );
              }}
            >
              Requests
            </button>
            <button
              type="button"
              className={`${cn.filterButton} ${
                lastFiveMinActive ? cn.active : ''
              }`}
              onClick={() => {
                const nextFilters = { ...filters };

                const dateTime = new Date().getTime();

                const lastFiveMinTime = dateTime - 1000 * 60 * 5;

                if (filters?.responseTime && lastFiveMinActive) {
                  delete nextFilters.responseTime;
                  setLastFiveMinActive(false);

                  localStorage.removeItem('responseTime');
                } else {
                  nextFilters.responseTime = {
                    type: SchemaFilterType.DATE_RANGE,
                    value: [
                      new Date(lastFiveMinTime).toISOString(),
                      new Date(dateTime).toISOString(),
                    ],
                  };

                  localStorage.setItem(
                    'responseTime',
                    JSON.stringify([
                      new Date(lastFiveMinTime).toISOString(),
                      new Date(dateTime).toISOString(),
                    ]),
                  );

                  setLastFiveMinActive(true);
                  setLastFiveteenMinActive(false);
                }

                onFilter(nextFilters, true);
              }}
            >
              Last 5 min
            </button>
            <button
              type="button"
              className={`${cn.filterButton} ${
                lastFiveteenMinActive ? cn.active : ''
              }`}
              onClick={() => {
                const nextFilters = { ...filters };

                const dateTime = new Date().getTime();

                const lastFiveteenMinTime = dateTime - 1000 * 60 * 15;

                if (filters?.responseTime && lastFiveteenMinActive) {
                  delete nextFilters.responseTime;
                  setLastFiveteenMinActive(false);
                  localStorage.removeItem('responseTime');
                } else {
                  nextFilters.responseTime = {
                    type: SchemaFilterType.DATE_RANGE,
                    value: [
                      new Date(lastFiveteenMinTime).toISOString(),
                      new Date(dateTime).toISOString(),
                    ],
                  };

                  localStorage.setItem(
                    'responseTime',
                    JSON.stringify([
                      new Date(lastFiveteenMinTime).toISOString(),
                      new Date(dateTime).toISOString(),
                    ]),
                  );

                  setLastFiveteenMinActive(true);
                  setLastFiveMinActive(false);
                }

                onFilter(nextFilters, true);
              }}
            >
              Last 15 min
            </button>
          </div>
          <div className={cn.integrationLogsTable}>
            <Table
              columns={integrationLogsColumns}
              tableTopClassName={cn.logsTableTop}
              data={formattedData || []}
              onSort={() => {}}
              filters={filters}
              sort={{}}
              columnsLabels={integrationLogsColumnsLabels}
              onFilterChange={(filterValues) => {
                onFilter(filterValues, true);
              }}
              error={errorList}
              filtersComponent={filtersComponent}
              handleSubmit={handleSubmit}
              isLoading={logsIsFetching}
              pagination={integrationLogsPagination}
              take="30"
              setPage={(data) => {
                setPage({
                  ...data,
                  filters: {
                    ...filters,
                  },
                });
              }}
              clickable
              withScroll
              withVerticalScroll
            />
          </div>
        </div>
      );
    }

    content = (
      <div
        className={`${cn.contentWrapper} ${cn.logsContentWrapper}`}
        ref={scrollRef}
      >
        <Card
          className={`${cn.documentsCard} ${
            Object.keys(filters)?.length ? cn.withFilters : ''
          } ${cn.integrationLogsCard}`}
          wrapperClassName={cn.externalCard}
          isLoading={isLoading}
          title={
            <div className={cn.externalAccountsTitle}>
              <span className={cn.externalAccountsTitleText}>
                {`${generalInfo?.companyName} - Integration log`}
              </span>
            </div>
          }
        >
          {tableContent}
        </Card>
        <Outlet />
      </div>
    );
  }

  return content;
};

export default PspIntegrationLogs;
